import { useCallback, useContext, useEffect, useMemo } from 'react';

import { SearchOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';

import { InputFactory } from '../../../../../../shared/components';

import { SavedQuotesContext } from '../../contexts/SavedQuotes';

import { dateIsBetween } from '../../../../../../shared/utils';
import { statesSavedQuotesFilter } from '../../utils/mocks';
import { operationSavedQuotesFilter } from '../../utils/mocks/states-saved-quotes-filter-form';

import { InputFactoryProp } from '../../../../../../shared/interfaces';

import { COLORS } from '../../../../../../config/themes/colors';

export const SavedQuotesFilter = () => {
  const { allDataTable, groupsList, savedQuotesForm, setDataTable } =
    useContext(SavedQuotesContext);

  const inputsFilter: InputFactoryProp[] = useMemo(
    () => [
      {
        element: 'input',
        type: 'text',
        placeHolder: 'Apellido y Nombre',
        icon: <SearchOutlined style={{ color: COLORS.neutrals[50] }} />,
        name: 'nameAndSurname',
      },
      {
        element: 'select',
        items: groupsList,
        name: 'article',
        placeHolder: 'Articulo',
        showSearch: true,
      },
      {
        element: 'date',
        name: 'entryDate',
      },
      {
        element: 'select',
        items: operationSavedQuotesFilter,
        name: 'operation',
        placeHolder: 'Operación',
      },
      {
        element: 'input',
        type: 'number',
        placeHolder: 'Número de Cotización',
        icon: <SearchOutlined style={{ color: COLORS.neutrals[50] }} />,
        name: 'quotationNumber',
      },
      {
        element: 'select',
        items: statesSavedQuotesFilter,
        placeHolder: 'Estado',
        name: 'state',
      },
    ],
    [groupsList]
  );

  const initialStateFilters = useMemo(
    () => ({
      article: undefined,
      entryDate: '',
      nameAndSurname: '',
      operation: undefined,
      quotationNumber: '',
      state: undefined,
    }),
    []
  );

  const onFilter = useCallback(() => {
    const {
      article,
      entryDate,
      nameAndSurname,
      quotationNumber,
      state,
      operation,
    } = savedQuotesForm.getFieldsValue();

    const stateSelected = statesSavedQuotesFilter.find(
      (element) => element.value === state
    )?.label;

    const dateFrom = entryDate?.[0]
      ? dayjs(entryDate[0]).format('DD-MM-YYYY')
      : undefined;

    const dateTo = entryDate?.[1]
      ? dayjs(entryDate[1]).format('DD-MM-YYYY')
      : undefined;

    const filteredSavedQuotes = allDataTable.filter((quotation) => {
      const matchesArticle = article
        ? article
            .trim()
            .toLowerCase()
            .includes(quotation.article.trim().toLowerCase())
        : true;

      const matchesName = nameAndSurname
        ? quotation.nameAndSurname
            .trim()
            .toLowerCase()
            .includes(nameAndSurname.trim().toLowerCase())
        : true;

      const matchesOperation = operation
        ? String(quotation.operation)
            .trim()
            .toLowerCase()
            .includes(String(operation).trim().toLowerCase())
        : true;

      const matchesQuotationNumber = quotationNumber
        ? quotation.quotationNumber
            .toString()
            .trim()
            .toLowerCase()
            .includes(quotationNumber.toString().trim().toLowerCase())
        : true;

      const matchesState = stateSelected
        ? quotation.state
            .trim()
            .toLowerCase()
            .includes(stateSelected.trim().toLowerCase())
        : true;

      const matchesDate =
        dateFrom && dateTo
          ? dateIsBetween(quotation.entryDate, dateFrom, dateTo, 'DD-MM-YYYY')
          : true;

      return (
        matchesArticle &&
        matchesName &&
        matchesOperation &&
        matchesQuotationNumber &&
        matchesState &&
        matchesDate
      );
    });

    setDataTable(filteredSavedQuotes);
  }, [savedQuotesForm, statesSavedQuotesFilter, groupsList, allDataTable]);

  const onClear = useCallback(() => {
    savedQuotesForm.setFieldsValue(initialStateFilters);
    onFilter();
  }, [savedQuotesForm, initialStateFilters]);

  useEffect(() => {
    savedQuotesForm.setFieldsValue(initialStateFilters);
  }, [savedQuotesForm, initialStateFilters]);

  return (
    <InputFactory
      form={savedQuotesForm}
      initialState={initialStateFilters}
      items={inputsFilter}
      onClear={onClear}
      onFilter={onFilter}
    />
  );
};
